import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../App';
import CreditMeter from './CreditMeter';
import { SERVICES, MOCK_AGENT, NAV_TABS } from '../data/constants';

const PROPERTY_TYPE_ICONS = {
  farm: '🌾', house: '🏠', apartment: '🏢',
  commercial: '🏪', plot: '📍', other: '📦',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { credits, plan, properties, activities, deductCredits, addActivity, user, logout } = useApp();

  const [activeTab,       setActiveTab]       = useState('overview');
  const [expandedService, setExpandedService] = useState(null);
  const [requestNote,     setRequestNote]     = useState('');
  const [justRequested,   setJustRequested]   = useState(null);

  const property = properties[0];
  const isLow    = credits < 2;
  const maxCr    = plan?.credits ?? 10;
  const caretaker = property?.caretaker_name
    ? { name: property.caretaker_name, phone: property.caretaker_phone }
    : null;

  function handleServiceClick(serviceId) {
    if (expandedService === serviceId) {
      setExpandedService(null);
      setRequestNote('');
    } else {
      setExpandedService(serviceId);
      setRequestNote('');
    }
  }

  function handleConfirmRequest(service) {
    deductCredits(service.cost);
    addActivity({
      service: service.name,
      icon:    service.icon,
      cost:    service.cost,
      note:    requestNote || `${service.name} requested.`,
    });
    setJustRequested(service.id);
    setExpandedService(null);
    setRequestNote('');
    setTimeout(() => setJustRequested(null), 3000);
  }

  /* Build the service grid rows.
     The inline form is injected into the grid at the correct position. */
  function renderServicesGrid(services) {
    const COLS = 4;
    const items = [];

    services.forEach((svc, idx) => {
      const isActive   = expandedService === svc.id;
      const isFlashing = justRequested  === svc.id;
      const insufficient = credits < svc.cost;

      items.push(
        <div
          key={svc.id}
          className={`service-card ${isActive ? 'active' : ''} ${isFlashing ? 'active' : ''}`}
          onClick={() => handleServiceClick(svc.id)}
        >
          <div className="service-card-icon">{svc.icon}</div>
          <div className="service-card-name">{svc.name}</div>
          <div className="service-card-desc">{svc.description}</div>
          <div className="service-card-cost">
            🪙 {svc.cost} {svc.cost === 1 ? 'credit' : 'credits'}
          </div>
          {svc.note && <div className="service-card-note">{svc.note}</div>}
        </div>
      );

      /* After each row (or the last item), inject the form if the
         expanded service is in this row */
      const isLastInRow  = (idx + 1) % COLS === 0;
      const isLastItem   = idx === services.length - 1;
      const rowStart     = Math.floor(idx / COLS) * COLS;
      const rowEnd       = rowStart + COLS - 1;
      const expandedIdx  = expandedService
        ? services.findIndex(s => s.id === expandedService)
        : -1;
      const formInThisRow =
        expandedService &&
        expandedIdx >= rowStart &&
        expandedIdx <= Math.min(rowEnd, services.length - 1);

      if ((isLastInRow || isLastItem) && formInThisRow) {
        const svcData = services.find(s => s.id === expandedService);
        if (svcData) {
          const afterBalance  = Math.round((credits - svcData.cost) * 10) / 10;
          const notEnough     = credits < svcData.cost;
          items.push(
            <div key="__form__" className="service-request-form">
              <div className="srf-header">
                <div className="srf-title">
                  {svcData.icon} Request: {svcData.name}
                </div>
                <button
                  className="srf-close"
                  onClick={e => { e.stopPropagation(); setExpandedService(null); }}
                >
                  ×
                </button>
              </div>

              <div className="srf-cost-row">
                <div className="srf-cost-item cost">
                  <div className="srf-cost-item-label">Service cost</div>
                  <div className="srf-cost-item-value">
                    🪙 {svcData.cost} {svcData.cost === 1 ? 'cr' : 'cr'}
                  </div>
                </div>
                <div className="srf-cost-item">
                  <div className="srf-cost-item-label">Your balance</div>
                  <div className="srf-cost-item-value">{credits} cr</div>
                </div>
                <div className={`srf-cost-item after ${notEnough ? 'insufficient' : ''}`}>
                  <div className="srf-cost-item-label">After request</div>
                  <div className="srf-cost-item-value">
                    {notEnough ? '—' : `${afterBalance} cr`}
                  </div>
                </div>
              </div>

              {svcData.note && (
                <div className="alert alert-amber" style={{ marginBottom: 12, fontSize: 13 }}>
                  ℹ️ {svcData.note}
                </div>
              )}

              <div className="form-group" onClick={e => e.stopPropagation()}>
                <label>Notes for your agent (optional)</label>
                <textarea
                  className="form-control"
                  placeholder={`Describe what you need for this ${svcData.name.toLowerCase()}…`}
                  value={requestNote}
                  onChange={e => setRequestNote(e.target.value)}
                  rows={3}
                />
              </div>

              {notEnough ? (
                <div className="srf-insufficient-alert" onClick={e => e.stopPropagation()}>
                  <span>⚠️ Not enough credits — you need {svcData.cost} cr but have {credits} cr.</span>
                  <button
                    className="btn btn-amber btn-sm"
                    onClick={() => navigate('/topup')}
                  >
                    Top Up Now
                  </button>
                </div>
              ) : (
                <div className="srf-footer" onClick={e => e.stopPropagation()}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setExpandedService(null); setRequestNote(''); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleConfirmRequest(svcData)}
                  >
                    Confirm Request — 🪙 {svcData.cost} cr
                  </button>
                </div>
              )}
            </div>
          );
        }
      }
    });

    return items;
  }

  /* ── TAB CONTENT ── */
  function renderTabContent() {
    if (activeTab !== 'overview') {
      const tab = NAV_TABS.find(t => t.id === activeTab);
      const tabServices = SERVICES.filter(s => s.tab === activeTab);
      if (!tabServices.length) {
        return (
          <div className="tab-placeholder">
            <div className="tab-placeholder-icon">{tab?.icon}</div>
            <h2>{tab?.label}</h2>
            <p>No services are configured for this section yet.</p>
            <button className="btn btn-outline" onClick={() => setActiveTab('overview')}>
              Back to Overview
            </button>
          </div>
        );
      }
      return (
        <>
          <div className="credit-meter-card">
            <div className="credit-meter-top">
              <div>
                <div className="credit-meter-title">{tab?.icon} {tab?.label}</div>
                <p className="text-muted text-sm" style={{ marginTop: 4 }}>
                  Services relevant to your {tab?.label.toLowerCase()}
                </p>
              </div>
              <button className="btn btn-amber btn-sm" onClick={() => navigate('/topup')}>
                + Top Up Credits
              </button>
            </div>
          </div>
          <div className="services-grid">
            {renderServicesGrid(tabServices)}
          </div>
        </>
      );
    }

    /* ── OVERVIEW ── */
    return (
      <>
        {/* Credit meter */}
        <div className="credit-meter-card">
          <div className="credit-meter-top">
            <div>
              <div className="credit-meter-title">Credits remaining</div>
              <div className="credit-meter-amount">
                <span
                  className="credit-meter-num"
                  style={{ color: isLow ? 'var(--red)' : credits < 5 ? 'var(--amber)' : 'var(--green)' }}
                >
                  {credits}
                </span>
                <span className="credit-meter-unit">credits</span>
              </div>
              {plan && (
                <div className="credit-meter-plan">
                  Plan: <strong>{plan.name}</strong> · {plan.credits} cr/mo included
                  · Extra at ${plan.extraCreditPrice}/cr
                </div>
              )}
            </div>
            <div className="credit-meter-actions">
              <button
                className="btn btn-amber"
                onClick={() => navigate('/topup')}
              >
                + Top Up Credits
              </button>
            </div>
          </div>
          <CreditMeter credits={credits} maxCredits={maxCr} showWarning={true} />
        </div>

        {/* Service cards */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Request a service</h2>
            <span className="text-sm text-muted">Click any card to request</span>
          </div>
          <div className="services-grid">
            {renderServicesGrid(SERVICES)}
          </div>
        </div>

        {/* Bottom row: agent + property + activity */}
        <div className="dash-bottom-row">
          {/* Agent card */}
          <div className="card agent-card">
            <div className="agent-avatar-wrap">
              <div className="agent-avatar">{MOCK_AGENT.initials}</div>
              <div>
                <div className="agent-name">{MOCK_AGENT.name}</div>
                <div className="agent-location">📍 {MOCK_AGENT.location}</div>
              </div>
            </div>
            <div className="agent-status">
              <div className="agent-status-dot" />
              Available now
            </div>
            <div className="agent-stats">
              <div className="agent-stat">
                <span className="agent-stat-value">⭐ {MOCK_AGENT.rating}</span>
                <span className="agent-stat-label">Rating</span>
              </div>
              <div className="agent-stat">
                <span className="agent-stat-value">{MOCK_AGENT.completedJobs}</span>
                <span className="agent-stat-label">Jobs done</span>
              </div>
            </div>
            <a
              href={`https://wa.me/${MOCK_AGENT.phone.replace(/\s+/g, '')}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline btn-full btn-sm"
              onClick={e => e.preventDefault()}
            >
              💬 Chat on WhatsApp
            </a>
          </div>

          {/* Property card */}
          {property && (
            <div className="card property-card">
              <div className="property-type-tag">
                {PROPERTY_TYPE_ICONS[property.type] || '📦'} {property.type}
              </div>
              <div className="property-name">{property.name}</div>
              <div className="property-location">📍 {property.location}</div>
              {caretaker && (
                <div className="property-caretaker">
                  <div className="property-caretaker-label">CARETAKER</div>
                  <div className="property-caretaker-name">{caretaker.name}</div>
                  <div className="property-caretaker-phone">{caretaker.phone}</div>
                </div>
              )}
              <button
                className="btn btn-ghost btn-sm btn-full"
                style={{ marginTop: 14 }}
              >
                + Add another property
              </button>
            </div>
          )}

          {/* Activity feed */}
          <div className="card activity-feed">
            <div className="activity-feed-title">
              <span>Recent activity</span>
              <span className="badge badge-green">{activities.length}</span>
            </div>
            {activities.length === 0 && (
              <p className="text-muted text-sm">No activity yet. Request your first service above.</p>
            )}
            {activities.slice(0, 8).map(a => (
              <div key={a.id} className="activity-item">
                <div className="activity-icon-wrap">{a.icon || '📋'}</div>
                <div className="activity-body">
                  <div className="activity-service">{a.service}</div>
                  <div className="activity-meta">
                    <span className="activity-date">{a.date}</span>
                    <span
                      className={`badge badge-sm ${
                        a.status === 'completed' ? 'badge-green'
                        : a.status === 'pending'   ? 'badge-amber'
                        : 'badge-gray'
                      }`}
                      style={{ fontSize: 11, padding: '2px 7px' }}
                    >
                      {a.status}
                    </span>
                    {a.note && (
                      <span className="activity-note">{a.note}</span>
                    )}
                  </div>
                </div>
                <div className="activity-cost">−{a.cost} cr</div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="dashboard">
      {/* ── TOP BAR ── */}
      <header className="dash-topbar">
        <div className="dash-logo">mudiaspora.</div>
        <div className="dash-topbar-right">
          <div className={`dash-credits-badge ${isLow ? 'low' : ''}`}>
            <span className="dash-credits-label">Credits</span>
            <span className="dash-credits-num">{credits}</span>
            <span className="dash-credits-unit">cr</span>
          </div>
          <button
            className="btn btn-amber btn-sm"
            onClick={() => navigate('/topup')}
          >
            + Top Up
          </button>
          {user?.full_name && (
            <span className="text-sm text-muted" style={{ fontWeight: 500 }}>
              {user.full_name}
            </span>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
          >
            Sign Out
          </button>
        </div>
      </header>

      <div className="dash-body">
        {/* ── SIDEBAR ── */}
        <aside className="dash-sidebar">
          {NAV_TABS.map(tab => (
            <div
              key={tab.id}
              className={`dash-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setExpandedService(null); }}
            >
              <span className="dash-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </div>
          ))}

          <div className="dash-sidebar-footer">
            <div className="dash-user-row">
              <div className="dash-avatar">
                {(user?.full_name?.[0] ?? 'M').toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {user?.full_name ?? plan?.name ?? 'User'}
                </div>
                <div className="text-muted text-xs">{plan?.name} · {credits} cr</div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
        <main className="dash-main">
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
}
